import {
  PostHogEvent,
  UserDeviceDetails,
  UserDevicesResult,
} from '@models/PostHog'
import { Sponsorship } from '@models/Sponsorship'
import { User, UserLegal, UserOnesignalId } from '@models/User'
import { SBAPI } from '@supplement-bacon/react-toolkit/utils'
import { TableParams } from '@supplement-bacon/sb-table-filters'
import { USERS_URL } from '@utils/urls'

const mapRawEventToPostHogEvent = (rawEvent: any): PostHogEvent =>
  ({
    uuid: rawEvent[0],
    event: rawEvent[1],
    properties: JSON.parse(rawEvent[2]),
    timestamp: rawEvent[3],
  }) as PostHogEvent

const fetchUsers = (params: TableParams) =>
  SBAPI.FetchPaginated<User[]>(USERS_URL, params)

const fetchUserById = (id: number, params?: TableParams) =>
  SBAPI.Fetch<User>(`${USERS_URL}/${id}`, params)

const updateUser = (user: User) => SBAPI.Put(`${USERS_URL}/${user.id}`, user)

const deleteUser = (userId: number) => SBAPI.Delete(`${USERS_URL}/${userId}`)

const fetchUserLegal = (userId: number) =>
  SBAPI.Fetch<UserLegal>(`${USERS_URL}/${userId}/legal`).then(
    (data) => data.data
  )

const fetchDevicesForUser = (userId: number) =>
  SBAPI.Fetch<UserDevicesResult>(`${USERS_URL}/${userId}/devices`).then(
    (data) => ({
      trashed: data.data.trashed,
      devices: data.data.devices,
    })
  )
const fetchDeviceDetailsForUser = (userId: number, deviceId: number) =>
  SBAPI.Fetch<UserDeviceDetails>(
    `${USERS_URL}/${userId}/devices/${deviceId}`
  ).then((data) => data.data)

const fetchEventsForUser = (userId: number) =>
  SBAPI.Fetch<PostHogEvent[]>(`${USERS_URL}/${userId}/events`).then((data) =>
    data.data.map(mapRawEventToPostHogEvent)
  )

const fetchOnesignalIdsForUser = (userId: number) =>
  SBAPI.Fetch<UserOnesignalId[]>(`${USERS_URL}/${userId}/onesignal-ids`).then(
    (data) => data.data
  )

const refreshReservationsForUser = (userId: number) =>
  SBAPI.Post(`${USERS_URL}/${userId}/reservations/refresh`, {}).then(
    (data) => data.data
  )

const getReferred = (userId: number) =>
  SBAPI.Fetch<Sponsorship[]>(`${USERS_URL}/${userId}/referred`).then(
    (data) => data.data
  )

const updateReferralCreditsForUser = (userId: number, credits: number) =>
  SBAPI.Put(`${USERS_URL}/${userId}/referral-credits`, { credits })

const updateLoyaltyTiersForUser = (userId: number, loyaltyTiers: number[]) =>
  SBAPI.Put(`${USERS_URL}/${userId}/loyalty-tiers`, { loyaltyTiers })

const updateSevenroomsProfile = (
  userId: number,
  updateProfiledata: {
    sevenrooms_client_id: string
    first_name?: string
    last_name?: string
    email?: string
  }
) =>
  SBAPI.Put(
    `${USERS_URL}/${userId}/third-parties/sevenrooms`,
    updateProfiledata
  )

const updateUserProviderProfile = (
  userId: number,
  userProviderProfileId: number,
  providerUserId: string
) =>
  SBAPI.Put(
    `${USERS_URL}/${userId}/provider-profiles/${userProviderProfileId}`,
    {
      providerUserId,
    }
  )

const triggerUsersExport = () => SBAPI.Post(`${USERS_URL}/export`, {})

export {
  deleteUser,
  fetchDeviceDetailsForUser,
  fetchDevicesForUser,
  fetchEventsForUser,
  fetchOnesignalIdsForUser,
  fetchUserById,
  fetchUserLegal,
  fetchUsers,
  getReferred,
  refreshReservationsForUser,
  triggerUsersExport,
  updateLoyaltyTiersForUser,
  updateReferralCreditsForUser,
  updateSevenroomsProfile,
  updateUser,
  updateUserProviderProfile,
}
